/**********************
 * Orders Types
 *********************/
import { atomWithSessionStorage } from '@/utils/browserStorage';
import { OrderFilter, OrderOrdering, QueryOrdersArgs } from 'gql-types';
import { useAtomValue } from 'jotai';
import _ from 'lodash';
import { useMemo } from 'react';
import { z } from 'zod';

import { RecurrenceUnit } from './recurrence';

/**********************
 * OrderTypes
 *********************/
export namespace Order {
    /** @deprecated Use OrderFilter imported from 'gql-types' */
    export type Filters = OrderFilter;
    /** @deprecated Use OrderOrdering imported from 'gql-types' */
    export type Ordering = OrderOrdering;
    /** @deprecated Use QueryOrdersArgs imported from 'gql-types' */
    export type ListVariables = QueryOrdersArgs;
}

/**********************
 * Order Quantity Types
 *********************/
export interface IOrderFrequency {
    intervalCount: number;
    intervalUnit: RecurrenceUnit;
    numVisitsPerInterval: number;
}

export function isSingletonFrequency(frequency: IOrderFrequency): boolean {
    return frequency.intervalCount === 1 && frequency.numVisitsPerInterval === 1;
}

export interface IOrderPrn {
    authorizedVisitCount: number;
}

/**
 * Checks if an order is currently discontinued.
 * An order is considered discontinued only if discontinuedAt is set AND the date is in the past.
 */
export function isOrderDiscontinued(discontinuedAt: Date | null): boolean {
    if (!discontinuedAt) {
        return false;
    }
    return new Date(discontinuedAt) <= new Date();
}

/** @deprecated Use OrderType imported from 'gql-types' */
export enum OrderType {
    FREQUENCY = 'FREQUENCY',
    PRN = 'PRN',
}

export function renderOrderType(orderType: OrderType): string {
    switch (orderType) {
        case OrderType.FREQUENCY:
            return 'Frequency';
        case OrderType.PRN:
            return 'PRN';
        default:
            return orderType;
    }
}

/**
 * Order Warnings
 */
export enum OrderWarning {
    HAS_ACTIVE_IN_REVIEW_VISITS = 'HAS_ACTIVE_IN_REVIEW_VISITS',
    DISCONTINUED_WITH_INCOMPLETE_OCCURRENCES = 'DISCONTINUED_WITH_INCOMPLETE_OCCURRENCES',
    EXCEEDS_MAX_ACTIVE_OCCURRENCES = 'EXCEEDS_MAX_ACTIVE_OCCURRENCES',
    EXCEEDS_MIN_ACTIVE_OCCURRENCES = 'EXCEEDS_MIN_ACTIVE_OCCURRENCES',
    INCOMPLETE_WITH_EXPIRED_FULFILLMENT_WINDOW = 'INCOMPLETE_WITH_EXPIRED_FULFILLMENT_WINDOW',
}

/**********************
 * Filter Helpers
 *********************/
export enum OrderStateFilter {
    ACTIVE = 'ACTIVE',
    PAUSED = 'PAUSED',
    DISCONTINUED = 'DISCONTINUED',
}

const orderFiltersSchema = z.object({
    patientIds: z.array(z.string()),
    disciplineIds: z.array(z.string()),
    orderTypes: z.array(z.nativeEnum(OrderType)),
    territoryIds: z.array(z.string()),
    // TODO(jessica): Clean up isDiscontinued and make orderState required after a while
    isDiscontinued: z.optional(z.array(z.boolean()).nullable()),
    orderState: z.optional(z.array(z.nativeEnum(OrderStateFilter)).nullable()),
});

export const orderFiltersAtom = atomWithSessionStorage(
    'orderFilters',
    {
        patientIds: [],
        disciplineIds: [],
        orderTypes: [],
        isDiscontinued: [],
        territoryIds: [],
        orderState: [],
    },
    orderFiltersSchema,
);
type OrderFiltersAtom = z.infer<typeof orderFiltersSchema>;

export const orderStatesToOrderStateFilter = (orderStates: OrderStateFilter[]) => {
    const filters: Order.Filters = {};
    if (orderStates.includes(OrderStateFilter.ACTIVE)) {
        filters.isActive = { exact: true };
    }
    if (orderStates.includes(OrderStateFilter.PAUSED)) {
        filters.isPaused = { exact: true };
    }
    if (orderStates.includes(OrderStateFilter.DISCONTINUED)) {
        filters.isDiscontinued = { exact: true };
    }
    return filters;
};
export const filtersFromFiltersAtom = (filterState: OrderFiltersAtom) => {
    const filters: Order.Filters = {};
    if (filterState.patientIds.length) {
        filters.patient = { id: { inList: filterState.patientIds } };
    }
    if (filterState.disciplineIds.length) {
        filters.discipline = { id: { inList: filterState.disciplineIds } };
    }
    if (filterState.orderTypes.length) {
        filters.orderType = { inList: filterState.orderTypes };
    }
    if (filterState.territoryIds.length) {
        filters.territories = { id: { inList: filterState.territoryIds } };
    }
    // Order State Filters
    if (filterState.orderState) {
        _.extend(filters, orderStatesToOrderStateFilter(filterState.orderState));
    }
    return filters;
};

export const useQueryFiltersFromFiltersAtom = () => {
    const filterState = useAtomValue(orderFiltersAtom);

    const queryFilters = useMemo(() => {
        return filtersFromFiltersAtom(filterState);
    }, [filterState]);

    const numFilters = _.size(
        _.filter(filterState, (orderFilterValue) =>
            orderFilterValue ? orderFilterValue.length > 0 : false,
        ),
    );

    return { queryFilters, numFilters };
};
